import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows, type KstDataGridPager } from '@/components/KstDataGrid';
import { AdminTabBar } from '@/components/AdminTabBar';
import { ConfirmModal } from '@/components/ConfirmModal';
import { AddUserModal, type AddUserSaveData } from '@/components/modals/AddUserModal';
import { UserHelpModal } from '@/components/modals/UserHelpModal';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { hasValue, hasStringValue } from '@/utils/legacy-utils';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';

import { deepCopyProperties, isUWPApp, isIOSApp, isAndroidApp, isWindowsAppSdkApp } from '@/utils/window-globals';
import { getCurrentCompanyId, getOfficerLabel, getOfficerPluralLabel } from '@/utils/auth';
import { dismissFeatureIntro } from '@/services/api';
import { SINGLE_USER_NUDGE, isFeatureIntroPending, clearPendingFeatureIntro } from '@/services/feature-intro/pending-intros';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getSubmoduleOrKerktonRoleFromLogEntry = _wg('getSubmoduleOrKerktonRoleFromLogEntry');

// AccountEnabledStates enum values
const ACCOUNT_NOT_EXPIRED = 0;
const ACCOUNT_DISABLED_BY_USER = 2;
const ACCOUNT_LOCKED_BY_USER = 3;

// Page size - large to get all users for client-side sorting
const PAGE_SIZE = 10000;

export function UserManagementScreen() {
  const { theme } = useTheme();

  const [activeTab, setActiveTab] = useState('showUserManagementTab');
  const [activePage, setActivePage] = useState('showUserManagementPage');

  // User entries
  const [userEntries, setUserEntries] = useState<any[] | null>(null);
  const [showingDisabledUsers, setShowingDisabledUsers] = useState(false);
  const showingDisabledRef = useRef(false);

  // Sorting
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Permission groups
  const [permissionGroups, setPermissionGroups] = useState<any[]>([]);

  // Audit log
  const [auditLogEntries, setAuditLogEntries] = useState<any[]>([]);
  const [auditLogPager, setAuditLogPager] = useState<KstDataGridPager>({
    curPage: undefined,
    numPages: undefined,
    records: undefined,
    isVisible: false,
  });

  // Caches
  const permissionGroupsCacheRef = useRef<any[] | null>(null);

  // Add User modal
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);

  // Onboarding nudge: shown while the company's SINGLE_USER_NUDGE intro is still
  // pending (server-side, company-scoped); dismissing hides it permanently.
  const [nudgeHidden, setNudgeHidden] = useState(() => !isFeatureIntroPending(SINGLE_USER_NUDGE));

  const dismissNudge = useCallback(() => {
    setNudgeHidden(true);
    clearPendingFeatureIntro(SINGLE_USER_NUDGE);
    // Fire-and-forget (matches the feature-intro modal flow): if the call fails,
    // the banner stays hidden for this session and returns on the next login.
    dismissFeatureIntro(SINGLE_USER_NUDGE).catch(() => {});
  }, []);

  // Help modal
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [helpModalTitle, setHelpModalTitle] = useState('');
  const [helpModalSubject, setHelpModalSubject] = useState('');
  const [helpModalMessage, setHelpModalMessage] = useState('');

  // Confirm modal (info dialog)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalTitle, setConfirmModalTitle] = useState('');
  const [confirmModalMessage, setConfirmModalMessage] = useState('');

  // Loading states
  const [isUsersLoading, setIsUsersLoading] = useState<boolean>(false);
  const [isAuditLogLoading, setIsAuditLogLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Company created before audit logging
  const companyCreatedBeforeAuditLogging = useMemo(() => getCurrentCompanyId() <= 998, []);

  const mobileTabs = [
    { id: 'showUserManagementTab', label: 'Users' },
    { id: 'showUserManagementUserProfilesTab', label: 'Profiles' },
    { id: 'showUserManagementAuditLogTab', label: 'Audit Log' },
  ];

  // Toggle button text
  const toggleButtonName = showingDisabledUsers ? 'Hide Disabled' : 'Show All';

  const userManagementHeaderButtons = useMemo(
    () => [
      { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
      { name: toggleButtonName, function: 'toggledisabled', src: '/Content/TourImages/list-tree.svg' },
    ],
    [toggleButtonName]
  );

  // Apply sorting to entries
  const applySorting = useCallback(
    (entries: any[], field: string, dir: string): any[] => {
      const sorted = [...entries];
      sorted.sort((a: any, b: any) => {
        let valA = a[field];
        let valB = b[field];
        if (valA == null) valA = '';
        if (valB == null) valB = '';
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();
        let result = 0;
        if (valA < valB) result = -1;
        else if (valA > valB) result = 1;
        return dir === 'asc' ? result : -result;
      });
      return sorted;
    },
    []
  );

  // Columns with inline HTML actions
  const userColumns: KstDataGridColumn[] = useMemo(
    () => [
      { field: 'id', name: 'ID', size: 'hidden' },
      {
        field: 'name',
        name: 'Name',
        size: 'col-xs-3',
        sortable: true,
        template: (e: any) => {
          if (hasValue(e) && hasValue(e.firstName)) {
            const enabledClass = e.enabledState !== ACCOUNT_NOT_EXPIRED ? 'kst-user-disabled' : '';
            return `<a href="/?sectionName=ADMIN&tab=UserProfile&function=ADMIN_UserProfile&params=${e.id}&multiModule=true" class="${enabledClass}">${e.name}</a>`;
          }
          return 'Error getting name.';
        },
      },
      {
        field: 'email',
        name: 'Primary Email',
        size: 'col-xs-3',
        sortable: true,
        template: (e: any) => {
          if (hasValue(e)) {
            const enabledClass = e.enabledState !== ACCOUNT_NOT_EXPIRED ? 'kst-user-disabled' : '';
            return `<span class="${enabledClass}">${e.email || ''}</span>`;
          }
          return '';
        },
      },
      {
        field: 'lastActive',
        name: 'Last Active',
        size: 'col-xs-2',
        sortable: true,
        template: (e: any) => {
          if (hasValue(e) && hasValue(e.lastActive)) {
            const enabledClass = e.enabledState !== ACCOUNT_NOT_EXPIRED ? 'kst-user-disabled' : '';
            const formattedDate = moment(e.lastActive).format('DD MMM YYYY  HH:mm');
            return `<span class="${enabledClass}">${formattedDate}</span>`;
          }
          return '';
        },
      },
      {
        field: 'profile',
        name: 'Profile',
        size: 'col-xs-1',
        template: (e: any) => {
          if (hasValue(e) && hasValue(e.id)) {
            return `<a href="/?sectionName=ADMIN&tab=UserProfile&function=ADMIN_UserProfile&params=${e.id}&multiModule=true">View</a>`;
          }
          return '';
        },
      },
      {
        field: 'registration',
        name: 'Password / Registration',
        size: 'col-xs-2',
        template: (e: any) => {
          if (!hasValue(e) || e.enabledState !== ACCOUNT_NOT_EXPIRED) {
            return '';
          }
          if (!e.isRegistered) {
            const buttonText = hasValue(e.registrationCodeSent) ? 'Re-send Registration' : 'Send Registration';
            return `<button type="button" class="kstc-btn kstc-btn--secondary kstc-btn--sm kst-resend-reg-btn" data-user-id="${e.id}">${buttonText}</button>`;
          }
          return '';
        },
      },
      {
        field: 'enabledState',
        name: 'User',
        size: 'col-xs-2',
        template: (e: any) => {
          if (!hasValue(e)) return '';
          const options = [
            { value: ACCOUNT_NOT_EXPIRED, label: 'Enabled' },
            { value: ACCOUNT_DISABLED_BY_USER, label: 'Disabled' },
            { value: ACCOUNT_LOCKED_BY_USER, label: 'Login Disabled' },
          ];
          let html = `<select class="kstc-select kstc-select--inline kst-user-enabled-select" data-user-id="${e.id}">`;
          for (const opt of options) {
            const selected = e.enabledState === opt.value ? 'selected' : '';
            html += `<option value="${opt.value}" ${selected}>${opt.label}</option>`;
          }
          html += '</select>';
          return html;
        },
      },
    ],
    []
  );

  const userMobileColumns: KstDataGridColumn[] = useMemo(
    () => [
      {
        field: 'email',
        name: 'Primary Email',
        size: 'col-xs-3',
        template: (e: any) => {
          if (hasValue(e)) {
            const enabledClass = e.enabledState !== ACCOUNT_NOT_EXPIRED ? 'kst-user-disabled' : '';
            return `<span class="${enabledClass}">${e.email || ''}</span>`;
          }
          return '';
        },
      },
      {
        field: 'lastActive',
        name: 'Last Active',
        size: 'col-xs-2',
        template: (e: any) => {
          if (hasValue(e) && hasValue(e.lastActive)) {
            const enabledClass = e.enabledState !== ACCOUNT_NOT_EXPIRED ? 'kst-user-disabled' : '';
            const formattedDate = moment(e.lastActive).format('DD MMM YYYY  HH:mm');
            return `<span class="${enabledClass}">${formattedDate}</span>`;
          }
          return '';
        },
      },
      {
        field: 'registration',
        name: 'Password / Registration',
        size: 'col-xs-2',
        template: (e: any) => {
          if (!hasValue(e) || e.enabledState !== ACCOUNT_NOT_EXPIRED) {
            return '';
          }
          if (!e.isRegistered) {
            const buttonText = hasValue(e.registrationCodeSent) ? 'Re-send Registration' : 'Send Registration';
            return `<button type="button" class="kstc-btn kstc-btn--secondary kstc-btn--sm kst-resend-reg-btn" data-user-id="${e.id}">${buttonText}</button>`;
          }
          return '';
        },
      },
      {
        field: 'enabledState',
        name: 'User',
        size: 'col-xs-2',
        template: (e: any) => {
          if (!hasValue(e)) return '';
          const options = [
            { value: ACCOUNT_NOT_EXPIRED, label: 'Enabled' },
            { value: ACCOUNT_DISABLED_BY_USER, label: 'Disabled' },
            { value: ACCOUNT_LOCKED_BY_USER, label: 'Login Disabled' },
          ];
          let html = `<select class="kstc-select kstc-select--inline kst-user-enabled-select" data-user-id="${e.id}">`;
          for (const opt of options) {
            const selected = e.enabledState === opt.value ? 'selected' : '';
            html += `<option value="${opt.value}" ${selected}>${opt.label}</option>`;
          }
          html += '</select>';
          return html;
        },
      },
    ],
    []
  );

  const auditLogColumns: KstDataGridColumn[] = useMemo(
    () => [
      {
        field: 'dateCreated',
        name: 'Date / Time',
        size: 'col-xs-2',
        template: (e: any) => {
          if (hasValue(e) && hasValue(e.dateCreated)) {
            return moment(e.dateCreated).format('MM/DD/YYYY HH:mm:ss');
          }
          return 'Error getting date / time.';
        },
      },
      {
        field: 'loggedByFirstName',
        name: 'First',
        size: 'col-xs-2',
        template: (e: any) => (hasValue(e) && hasValue(e.loggedByFirstName) ? e.loggedByFirstName : ''),
      },
      {
        field: 'loggedByLastName',
        name: 'Last',
        size: 'col-xs-2',
        template: (e: any) => (hasValue(e) && hasValue(e.loggedByLastName) ? e.loggedByLastName : ''),
      },
      {
        field: 'role',
        name: 'Role',
        size: 'col-xs-1',
        template: (e: any) => (hasValue(e) && hasValue(e.role) ? getSubmoduleOrKerktonRoleFromLogEntry(e) : ''),
      },
      {
        field: 'action',
        name: 'Action',
        size: 'col-xs-1',
        template: (e: any) => (hasValue(e) && hasValue(e.action) ? e.action : ''),
      },
      {
        field: 'entry',
        name: 'Entry',
        size: 'col-xs-4',
        template: (e: any) => (hasValue(e) && hasValue(e.entry) ? e.entry : ''),
      },
    ],
    []
  );

  const userRows: KstDataGridRows = useMemo(() => ({ entries: userEntries }), [userEntries]);
  const auditLogRowsData: KstDataGridRows = useMemo(() => ({ entries: auditLogEntries }), [auditLogEntries]);

  const pagerHidden: KstDataGridPager = useMemo(() => ({ isVisible: false }), []);

  // Cache helpers
  const getPermissionGroupList = useCallback((): Promise<any[]> => {
    return new Promise((resolve) => {
      if (hasValue(permissionGroupsCacheRef.current)) {
        resolve(deepCopyProperties(permissionGroupsCacheRef.current));
      } else {
        api.getPermissionGroups((result: any) => {
          permissionGroupsCacheRef.current = result;
          resolve(deepCopyProperties(result));
        });
      }
    });
  }, []);

  const getUserList = useCallback(
    (showDisabled: boolean): Promise<any> => {
      return new Promise((resolve) => {
        const requestData = {
          data: {
            take: PAGE_SIZE,
            skip: 0,
            page: 1,
            pageSize: PAGE_SIZE,
            filter: {
              logic: 'and',
              filters: showDisabled ? [] : [{ field: 'isEnabled', operator: 'eq', value: true }],
            },
          },
        };
        api.getUserManagementList(1, PAGE_SIZE.toString(), JSON.stringify(requestData), (result: any) => {
          resolve(result);
        });
      });
    },
    []
  );

  const refreshUserList = useCallback(
    (currentSortField?: string, currentSortDir?: string) => {
      const sf = currentSortField || sortField;
      const sd = currentSortDir || sortDirection;

      setIsUsersLoading(true);
      Promise.all([getUserList(showingDisabledRef.current), getPermissionGroupList()]).then((values) => {
        const userResponse: any = values[0];
        const groups: any = values[1];

        const entries = userResponse.entries || [];
        for (const entry of entries) {
          entry.name = entry.firstName + ' ' + entry.lastName;
          if (hasStringValue(entry.callSign)) {
            entry.name += ' (' + entry.callSign + ')';
          }
          entry.isChecked = false;
        }

        const sorted = applySorting(entries, sf, sd);
        setUserEntries(sorted);
        setPermissionGroups(groups);
        setIsUsersLoading(false);
      }).catch(() => {
        setIsUsersLoading(false);
      });
    },
    [getUserList, getPermissionGroupList, applySorting, sortField, sortDirection]
  );

  const getAuditLogPage = useCallback((page: number) => {
    setIsAuditLogLoading(true);
    api.getUserManagementAuditLogEntries(
      { data: { page, pageSize: 30 } },
      (result: any) => {
        const response = deepCopyProperties(result);
        const entries: any[] = [];
        for (let i = 0; i < response.logs.length; i++) {
          const entry = response.logs[i];
          entry.isChecked = false;
          entries.push(entry);
        }
        setAuditLogEntries(entries);
        setAuditLogPager({
          curPage: response.curPage,
          numPages: response.numPages,
          records: response.records,
          isVisible: true,
        });
        setIsAuditLogLoading(false);
      },
      () => {
        App.showPopupNotification('Error getting user management audit log.', 'error');
        setIsAuditLogLoading(false);
      }
    );
  }, []);

  // Resend registration handler
  const resendRegistration = useCallback(
    (userId: number) => {
      api.resendRegistration(
        userId,
        () => {
          App.showPopupNotification('Registration email sent.', 'success');
          refreshUserList();
        },
        () => {
          App.showPopupNotification('Error re-sending email.', 'error');
        }
      );
    },
    [refreshUserList]
  );

  // Update user enabled state handler
  const updateUserEnabledState = useCallback(
    (userId: number, enabledState: number) => {
      api.updateUser(userId, enabledState, () => {
        refreshUserList();
      });
    },
    [refreshUserList]
  );

  // Event delegation for inline HTML actions
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('kst-resend-reg-btn')) {
        e.preventDefault();
        const userId = parseInt(target.getAttribute('data-user-id') || '0', 10);
        if (userId) {
          resendRegistration(userId);
        }
      }
    };
    const handleChange = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('kst-user-enabled-select')) {
        const userId = parseInt(target.getAttribute('data-user-id') || '0', 10);
        const newState = parseInt((target as HTMLSelectElement).value, 10);
        if (userId) {
          updateUserEnabledState(userId, newState);
        }
      }
    };
    document.addEventListener('click', handleClick);
    document.addEventListener('change', handleChange);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('change', handleChange);
    };
  }, [resendRegistration, updateUserEnabledState]);

  // Load initial data
  useEffect(() => {
    refreshUserList();
  }, []);

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      if (tabId === 'showUserManagementTab') {
        setActivePage('showUserManagementPage');
        refreshUserList();
      } else if (tabId === 'showUserManagementUserProfilesTab') {
        setActivePage('showUserManagementUserProfilesPage');
      } else if (tabId === 'showUserManagementAuditLogTab') {
        setActivePage('showUserManagementAuditLogPage');
        getAuditLogPage(1);
      }
    },
    [refreshUserList, getAuditLogPage]
  );

  const handleButtonCommand = useCallback(
    (functionName: string) => {
      if (functionName === 'add') {
        setAddUserModalOpen(true);
      } else if (functionName === 'toggledisabled') {
        const newVal = !showingDisabledRef.current;
        showingDisabledRef.current = newVal;
        setShowingDisabledUsers(newVal);
        refreshUserList();
      }
    },
    [refreshUserList]
  );

  const handleAddUserSave = useCallback(
    (data: AddUserSaveData) => {
      const newUser = {
        lastName: data.lastName,
        firstName: data.firstName,
        isLoginEnabled: data.isLoginEnabled,
        email: data.email,
        permissionGroupIds: data.permissionGroupIds,
      };
      setIsSaving(true);
      api.saveUserManagement(
        newUser,
        () => {
          setIsSaving(false);
          const nameOfNewUser = data.firstName + ' ' + data.lastName;
          App.showPopupNotification('User Added: ' + nameOfNewUser, 'success');
          setAddUserModalOpen(false);
          refreshUserList();
        },
        () => {
          setIsSaving(false);
          App.showPopupNotification('Error Adding User', 'error');
        }
      );
    },
    [refreshUserList]
  );

  const handleSort = useCallback(
    (field: string) => {
      let newDir: 'asc' | 'desc' = 'asc';
      if (sortField === field) {
        newDir = sortDirection === 'asc' ? 'desc' : 'asc';
      }
      setSortField(field);
      setSortDirection(newDir);

      const sorted = applySorting(userEntries, field, newDir);
      setUserEntries(sorted);
    },
    [sortField, sortDirection, userEntries, applySorting]
  );

  const downloadUserProfilesCsv = useCallback((includeDisabledUsers: boolean) => {
    api.getUserManagementDownloadUserProfilesCsv(
      includeDisabledUsers,
      (result: any) => {
        const csv: string = Papa.unparse({
          fields: result.header,
          data: result.records,
        });
        const filename = `Orna User Profiles - ${moment().format('YYYY-MM-DD')}.csv`;
        if (isUWPApp || isIOSApp || isAndroidApp || isWindowsAppSdkApp) {
          App.showPopupNotification('Not available on apps. Must download from web (https://orna.kerkton.com).', 'error');
        } else {
          const blob = new Blob([csv], { type: 'text/csv' });
          saveAs(blob, filename);
        }
      },
      () => {
        App.showPopupNotification('Error getting CSV download.', 'error');
      }
    );
  }, []);

  return (
    <div className={theme}>
      {/* Modals */}
      <AddUserModal
        open={addUserModalOpen}
        saving={isSaving}
        permissionGroups={permissionGroups}
        theme={theme}
        onSave={handleAddUserSave}
        onCancel={() => setAddUserModalOpen(false)}
      />

      <UserHelpModal
        open={helpModalOpen}
        title={helpModalTitle}
        subject={helpModalSubject}
        message={helpModalMessage}
        theme={theme}
        onClose={() => setHelpModalOpen(false)}
      />

      <ConfirmModal
        open={confirmModalOpen}
        title={confirmModalTitle}
        message={confirmModalMessage}
        showCancel={false}
        confirmText="OK"
        theme={theme}
        onConfirm={() => setConfirmModalOpen(false)}
        onCancel={() => setConfirmModalOpen(false)}
      />

      <div className="kst-page-container">
        {/* Mobile Tab Navigation */}
        <div className="kst-mobile-nav">
          <AdminTabBar tabs={mobileTabs} activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className="kst-layout">
          {/* Desktop Sidebar Navigation */}
          <div className="kst-sidebar">
            <ul className="kst-sidebar-list">
              <li
                className={`kst-sidebar-item${activeTab === 'showUserManagementTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showUserManagementTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="kst-sidebar-text">Users</span>
              </li>
              <li
                className={`kst-sidebar-item${activeTab === 'showUserManagementUserProfilesTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showUserManagementUserProfilesTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="7,10 12,15 17,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span className="kst-sidebar-text">Profiles</span>
              </li>
              <li
                className={`kst-sidebar-item${activeTab === 'showUserManagementAuditLogTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showUserManagementAuditLogTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="kst-sidebar-text">Audit Log</span>
              </li>
            </ul>
          </div>

          {/* Main Content Area */}
          <div className="kst-main-content">
            {/* User Management Page */}
            {activePage === 'showUserManagementPage' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">User Management</h3>
                  <p className="kst-page-description">
                    Manage users for your organization. Create new users, view profiles, and control user access.
                  </p>
                </div>

                {/* Onboarding nudge. The list endpoint excludes Kerkton support users
                    (PersonService.getPersonManagementList), so a fresh company shows
                    exactly 1 entry: the signup admin. 1 visible user = "no team yet".
                    Gated on the SINGLE_USER_NUDGE feature intro so a solo-operator
                    company can dismiss it for good. */}
                {!nudgeHidden && !isUsersLoading && userEntries !== null && userEntries.length === 1 && (
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12,
                      border: '1px solid #3b82f6', borderRadius: 8, padding: '10px 14px', marginBottom: 12,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ fontWeight: 600 }}>Just you so far?</div>
                      <div style={{ fontSize: 12.5, opacity: 0.8 }}>
                        Invite your {getOfficerPluralLabel().toLowerCase()} and dispatchers. Each gets an
                        email to set up their own login, then they can take calls, run tours, and file
                        reports from their device.
                      </div>
                    </div>
                    <button type="button" className="btn-action" onClick={() => setAddUserModalOpen(true)}>
                      + Invite your first {getOfficerLabel()}
                    </button>
                    <button
                      type="button"
                      onClick={dismissNudge}
                      style={{
                        background: 'none', border: 'none', padding: '4px 2px', cursor: 'pointer',
                        color: 'inherit', opacity: 0.65, fontSize: 12.5, textDecoration: 'underline',
                      }}
                    >
                      Don't show this again
                    </button>
                  </div>
                )}

                <KstDataGrid
                  columns={userColumns}
                  rows={userRows}
                  headerbuttons={userManagementHeaderButtons}
                  pager={pagerHidden}
                  mobilecolumns={userMobileColumns}
                  mobiletitlefield="name"
                  showcheckbox={false}
                  sortfield={sortField}
                  sortdirection={sortDirection}
                  loading={isUsersLoading}
                  onButtonCommand={handleButtonCommand}
                  onSort={handleSort}
                />
              </div>
            )}

            {/* User Profiles Page */}
            {activePage === 'showUserManagementUserProfilesPage' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">User Profiles</h3>
                  <p className="kst-page-description">
                    Download a CSV export of user profiles.
                  </p>
                </div>

                <div className="kst-settings-form">
                  <div className="kstc-form-group">
                    <label className="kstc-label">Download CSV of User Profiles (web only)</label>
                    <div className="kst-download-buttons">
                      <button
                        type="button"
                        className="kstc-btn kstc-btn--secondary"
                        onClick={() => downloadUserProfilesCsv(false)}
                      >
                        User Profiles
                      </button>
                      <button
                        type="button"
                        className="kstc-btn kstc-btn--secondary"
                        onClick={() => downloadUserProfilesCsv(true)}
                      >
                        User Profiles Including Disabled Users
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Audit Log Page */}
            {activePage === 'showUserManagementAuditLogPage' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">User Management Audit Log</h3>
                </div>

                {companyCreatedBeforeAuditLogging && (
                  <p className="kst-audit-log-note">
                    Audit Log includes information from February 17, 2025 and later.
                  </p>
                )}

                <KstDataGrid
                  columns={auditLogColumns}
                  rows={auditLogRowsData}
                  pager={auditLogPager}
                  mobiletitlefield="entry"
                  showcheckbox={false}
                  showheader={false}
                  loading={isAuditLogLoading}
                  onGoToPage={getAuditLogPage}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserManagementScreen;

